<template>
  <section
    v-if="visible && (error || working || draft || job)"
    class="mt-5"
    aria-label="Account deactivation"
  >
    <p v-if="error" class="text-red mb-3" role="alert">
      {{ error }}
      <button
        v-if="statusError"
        type="button"
        class="btn btn-outline-secondary ml-2"
        :disabled="working"
        @click="restore"
      >
        Retry status check
      </button>
    </p>
    <p v-if="working" role="status">Preparing account review…</p>
    <form
      v-if="draft"
      class="box p-6 border-2 border-theme-6"
      @submit.prevent="confirm"
    >
      <h3 class="text-lg font-medium text-red">Confirm account deactivation</h3>
      <p class="mt-3">
        Deactivate {{ draft.total }} selected account{{
          draft.total === 1 ? "" : "s"
        }}? These users will lose access. Their teams, donations and history
        will be preserved. An administrator can reactivate accounts later.
      </p>
      <p class="mt-2">
        History reviewed: {{ draft.reviewWindow.years.join(" and ") }}. This
        preview expires after 15 minutes.
      </p>
      <ul class="mt-4 list-disc ml-5">
        <li v-for="target in previewTargets" :key="target.userId">
          {{ target.name }} — {{ target.email }}
        </li>
      </ul>
      <div v-if="draft.total > 25" class="flex gap-3 items-center mt-3">
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="previewPage === 1"
          @click="previewPage--"
        >
          Previous accounts
        </button>
        <span>{{ previewPage }} / {{ Math.ceil(draft.total / 25) }}</span>
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="previewPage * 25 >= draft.total"
          @click="previewPage++"
        >
          Next accounts
        </button>
      </div>
      <details v-if="draft.exclusions.length" class="mt-4">
        <summary
          >{{ draft.exclusions.length }} selected accounts excluded</summary
        >
        <ul class="list-disc ml-5">
          <li v-for="item in draft.exclusions" :key="item.userId">
            {{ item.name }}: {{ item.reason }}
          </li>
        </ul>
      </details>
      <label class="block mt-5" for="deactivation-confirmation"
        >Type <strong>{{ draft.confirmation }}</strong> to confirm.</label
      >
      <input
        id="deactivation-confirmation"
        v-model="phrase"
        autocomplete="off"
        type="text"
        class="form-control mt-2"
        :disabled="working"
      />
      <div class="flex gap-3 mt-4">
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="working"
          @click="cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="btn btn-danger"
          :disabled="working || phrase !== draft.confirmation"
        >
          Confirm and deactivate
        </button>
      </div>
    </form>
    <button
      v-if="job && !running"
      type="button"
      class="text-theme-1 underline"
      :aria-expanded="historyOpen"
      aria-controls="deactivation-history"
      @click="historyOpen = !historyOpen"
    >
      {{ historyOpen ? "Hide deactivation history" : "Deactivation history" }}
    </button>
    <div
      v-if="job && (running || historyOpen)"
      id="deactivation-history"
      class="box p-5 mt-4"
      aria-live="polite"
    >
      <h3 class="font-medium">Latest account deactivation — {{ jobLabel }}</h3>
      <p class="mt-2">
        {{ job.succeeded }} deactivated · {{ job.skipped }} skipped ·
        {{ job.failed }} failed · {{ job.pending }} remaining
      </p>
      <progress
        class="w-full mt-3"
        :value="job.total - job.pending"
        :max="job.total || 1"
        aria-label="Deactivation progress"
      />
      <p v-if="running" class="mt-2">
        Running in the background. You can leave this page and return to check
        progress.
      </p>
      <p v-if="job.message" class="mt-2">{{ job.message }}</p>
      <details v-if="job.skipped || job.failed" class="mt-3">
        <summary>Skipped and failed accounts</summary>
        <ul class="list-disc ml-5">
          <li v-for="item in outcomes" :key="item.userId">
            {{ item.name }}: {{ item.reason }}
          </li>
        </ul>
      </details>
      <button
        v-if="!running && remainingIds.length"
        type="button"
        class="btn btn-outline-danger mt-4"
        :disabled="working"
        @click="review({ userIds: remainingIds })"
      >
        Review remaining accounts again
      </button>
    </div>
  </section>
</template>
<script>
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  watch
} from "vue";
import {
  previewDeactivation,
  startDeactivation,
  getDeactivationJob,
  getLatestDeactivation
} from "@/api/user-deactivations";
import { errorMessage } from "@/composables/use-admin-list";

export default defineComponent({
  props: {
    request: { type: Object, default: null },
    visible: { type: Boolean, default: true }
  },
  emits: ["completed", "busy-change"],
  setup(props, { emit }) {
    const draft = ref(null);
    const job = ref(null);
    const historyOpen = ref(false);
    watch(
      () => props.visible,
      () => {
        historyOpen.value = false;
      }
    );
    watch(
      () => job.value && job.value.id,
      () => {
        historyOpen.value = false;
      }
    );
    const error = ref("");
    const statusError = ref(false);
    const working = ref(false);
    const phrase = ref("");
    const previewPage = ref(1);
    const running = computed(
      () => !!job.value && ["queued", "running"].includes(job.value.status)
    );
    const jobLabel = computed(
      () =>
        ({
          queued: "Waiting to start",
          running: "In progress",
          completed: "Complete",
          completed_with_errors: "Complete with failures",
          interrupted: "Needs a new review"
        }[job.value && job.value.status])
    );
    const previewTargets = computed(() =>
      draft.value
        ? draft.value.targets.slice(
            (previewPage.value - 1) * 25,
            previewPage.value * 25
          )
        : []
    );
    const outcomes = computed(() =>
      job.value
        ? job.value.targets.filter(item =>
            ["skipped", "failed"].includes(item.status)
          )
        : []
    );
    const remainingIds = computed(() =>
      job.value
        ? job.value.targets
            .filter(item => ["pending", "failed"].includes(item.status))
            .map(item => item.userId)
        : []
    );
    let disposed = false;
    let revision = 0;
    let polling = false;
    let reported = "";
    const cancel = () => {
      draft.value = null;
      phrase.value = "";
    };
    const restore = async () => {
      const stamp = revision;
      try {
        const latest = await getLatestDeactivation();
        if (!disposed && stamp === revision) {
          const finished =
            running.value &&
            latest &&
            latest.id === job.value.id &&
            !["running", "queued"].includes(latest.status);
          job.value = latest;
          error.value = "";
          statusError.value = false;
          if (finished && reported !== latest.id) {
            reported = latest.id;
            emit("completed");
          }
        }
      } catch (err) {
        if (!disposed && stamp === revision) {
          error.value =
            "Unable to check deactivation progress. Please try again.";
          statusError.value = true;
        }
      }
    };
    const review = async request => {
      if (working.value || running.value) return;
      const current = ++revision;
      cancel();
      working.value = true;
      error.value = "";
      statusError.value = false;
      try {
        const result = await previewDeactivation(request);
        if (current === revision && !disposed) {
          draft.value = result;
          previewPage.value = 1;
        }
      } catch (err) {
        if (!disposed) error.value = errorMessage(err);
      } finally {
        if (!disposed) working.value = false;
      }
    };
    const confirm = async () => {
      if (
        !draft.value ||
        working.value ||
        phrase.value !== draft.value.confirmation
      )
        return;
      working.value = true;
      revision += 1;
      try {
        const result = await startDeactivation(draft.value.id, phrase.value);
        if (!disposed) {
          job.value = result;
          cancel();
          error.value = "";
          statusError.value = false;
        }
      } catch (err) {
        if (!disposed) {
          cancel();
          await restore();
          error.value = errorMessage(err);
        }
      } finally {
        if (!disposed) working.value = false;
      }
    };
    watch(
      () => props.request,
      request => {
        if (request) review(request);
        else {
          revision += 1;
          cancel();
        }
      }
    );
    watch(
      () => working.value || running.value || !!draft.value,
      value => emit("busy-change", value),
      { immediate: true }
    );
    const poll = async () => {
      if (!running.value || polling || disposed) return;
      polling = true;
      try {
        const current = job.value.id;
        const result = await getDeactivationJob(current);
        if (!disposed && job.value && job.value.id === current) {
          job.value = result;
          error.value = "";
          statusError.value = false;
          if (
            !["running", "queued"].includes(result.status) &&
            reported !== current
          ) {
            reported = current;
            emit("completed");
          }
        }
      } catch (err) {
        if (!disposed) {
          error.value =
            "Unable to check deactivation progress. Please try again.";
          statusError.value = true;
        }
      } finally {
        polling = false;
      }
    };
    let timer;
    onMounted(() => {
      restore();
      timer = setInterval(poll, 5000);
    });
    onUnmounted(() => {
      disposed = true;
      revision += 1;
      clearInterval(timer);
    });
    return {
      draft,
      job,
      historyOpen,
      error,
      statusError,
      working,
      phrase,
      previewPage,
      previewTargets,
      running,
      jobLabel,
      outcomes,
      remainingIds,
      confirm,
      cancel,
      review,
      restore
    };
  }
});
</script>
<style scoped>
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
